import {yupResolver} from '@hookform/resolvers/yup';
import moment from 'moment';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import {
  ActivityIndicator,
  SafeAreaView,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from '@react-native-vector-icons/fontawesome';
import {useSelector} from 'react-redux';
import * as yup from 'yup';
import {ShowToast, retrieveSetting} from '../Common/common';
import BTButton from '../Components/BTButton';
import BTComboBox from '../Components/BTComboBox';
import {Col, Row} from '../Components/BTGrid';
import {TextInput} from '../Components/BTTextInput';
import {useGetPOAOutletsQuery} from '../redux/services/poaoutletApi';
import {useInsertPOECancelOrderMutation} from '../redux/services/poecancelorderApi';
import {
  useGetPOEOrderByIdQuery,
  useGetPOKOTsQuery,
} from '../redux/services/poeorderApi';
import {RootState} from '../redux/store';
import styles from '../styles/Styles';

import T from '../Components/T';
import { tp } from '../utils/translateText';
const validationSchema = yup.object().shape({
  co_kot: yup.number().required('Select KOT No.').min(1, 'Select KOT No.'),
  co_remarks: yup.string().required('Remarks is required'),
  co_details: yup.array().of(
    yup.object().shape({
      cd_qty: yup.number().required('Cancel Quantity is required'),
    }),
  ),
});

interface CancelOrderItem {
  cd_id: number;
  cd_srid: number;
  cd_prod: number;
  cd_descr: string;
  cd_instruct: string;
  cd_qty: string;
  cd_rate: number;
  cd_printer: number;
  cd_oqty: string;
}

const POSCancelKOT = ({navigation, route}: UniversalProps) => {
  const gdata = useSelector((state: RootState) => state.gdata);
  const {data: outletData, isLoading: isLoadingOutlets} = useGetPOAOutletsQuery(
    gdata.company,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [selKot, setSelKot] = useState(0);
  const [defOutlet, setDefOutlet] = useState(13);
  const [defArea, setDefArea] = useState('');

  // Initialize default outlet
  useEffect(() => {
    const retDefOutlet = async () => {
      const outlet = await retrieveSetting('defoutlet');
      setDefOutlet(Number(outlet) || 13);
    };
    retDefOutlet();
  }, []);

  // Initialize default area
  useEffect(() => {
    const retDefArea = async () => {
      const area = await retrieveSetting('defarea');
      setDefArea(area || '');
    };
    retDefArea();
  }, []);

  // Format outlets for dropdown
  const outlets = useMemo(() => {
    return (
      outletData?.map(value => ({
        label: value.ol_sname,
        id: value.ol_id,
        value: value,
      })) || []
    );
  }, [outletData]);

  // Initial form values
  const initialValues = useMemo(
    () => ({
      co_id: 0,
      co_date: moment().format('YYYY/MM/DD HH:mm:ss'),
      co_company: gdata.company,
      co_outlet: defOutlet,
      co_area: defArea,
      co_table: route.params?.table?.tb_id ?? 0,
      co_kot: 0,
      co_remarks: '',
      co_uid: gdata.user,
      co_cdt: moment().format('YYYY/MM/DD HH:mm:ss'),
      co_muid: gdata.user,
      co_mdt: moment().format('YYYY/MM/DD HH:mm:ss'),
      co_details: [] as CancelOrderItem[],
    }),
    [gdata.company, gdata.user, defOutlet, defArea, route.params?.table?.tb_id],
  );

  const {...methods} = useForm<any>({
    defaultValues: initialValues,
    resolver: yupResolver(validationSchema),
  });

  // Update form when default outlet changes
  useEffect(() => {
    methods.setValue('co_outlet', defOutlet);
  }, [defOutlet, methods]);

  // Update form when default area changes
  useEffect(() => {
    methods.setValue('co_area', Number(defArea));
  }, [defArea, methods]);

  const {control} = methods;

  const {fields, append, remove} = useFieldArray({
    name: 'co_details',
    control,
  });

  // Fetch KOTs data
  const {data: kotsData, isLoading: isLoadingKOTs} = useGetPOKOTsQuery(
    {
      cid: gdata.company,
      tbl: route.params?.table?.tb_id ?? 0,
    }
  );

  // Fetch order data
  const {data: orderData, isLoading: isLoadingOrder} = useGetPOEOrderByIdQuery(
    selKot.toString()
  );

  // Update order details when order data changes
  useEffect(() => {
    if (!orderData?.or_details) return;

    remove();

    orderData.or_details.forEach((v: po_eorderdetails) => {
      append({
        cd_id: v.od_id,
        cd_srid: v.od_srid,
        cd_prod: v.od_prod,
        cd_descr: v.od_descr,
        cd_instruct: v.od_instruct,
        cd_qty: '0',
        cd_rate: v.od_rate,
        cd_printer: v.od_printer,
        cd_oqty: v.od_qty,
      });
    });
  }, [orderData, append, remove]);

  // Format KOTs for dropdown
  const kots = useMemo(() => {
    return (
      kotsData?.map(value => ({
        label: value.or_no || '-',
        id: value.or_id,
        table: value,
      })) || []
    );
  }, [kotsData]);

  const [insertPOECancelOrder] = useInsertPOECancelOrderMutation();

  const onSubmit: SubmitHandler<po_ecancelorder> = useCallback(
    async values => {
      if (isSaving) return;

      try {
        setIsSaving(true);
        await insertPOECancelOrder(values).unwrap();
        methods.reset();
        ShowToast('Order Cancelled Successfully');
        setIsSaving(false);
        navigation.navigate('Tables');
      } catch (e: any) {
        ShowToast(e?.data?.message || e?.message || 'An error occurred', 'error');
        setIsSaving(false);
      }
    },
    [isSaving, insertPOECancelOrder, methods, navigation],
  );

  const onError: SubmitErrorHandler<po_ecancelorder> = useCallback(
    (errors, e) => {
      ShowToast('Please fill all required details', 'error');
      return false;
    },
    [],
  );

  // Handle decrease quantity
  const handleDecreaseQty = useCallback(
    (index: number) => {
      const currentQty = Number(
        methods.getValues(`co_details.${index}.cd_qty`),
      );
      if (currentQty > 0) {
        methods.setValue(
          `co_details.${index}.cd_qty`,
          (currentQty - 1).toString(),
        );
      }
    },
    [methods],
  );

  // Handle increase quantity
  const handleIncreaseQty = useCallback(
    (index: number) => {
      const currentQty = Number(
        methods.getValues(`co_details.${index}.cd_qty`),
      );
      const originalQty = Number(
        methods.getValues(`co_details.${index}.cd_oqty`),
      );
      if (currentQty < originalQty) {
        methods.setValue(
          `co_details.${index}.cd_qty`,
          (currentQty + 1).toString(),
        );
      }
    },
    [methods],
  );

  const isLoading = isLoadingKOTs || isLoadingOrder || isLoadingOutlets;

  return (
    <ScrollView
      keyboardShouldPersistTaps="handled"
      style={styles.app}
      contentContainerStyle={{
        flexGrow: 1,
        paddingBottom: 80,
      }}>
      {/* Modern Header */}
      <View style={styles.modernHeaderContainer}>
        <Text style={styles.modernHeaderTitle}><T>Cancel Order Entry</T></Text>
      </View>
        {/* Loading State */}
        {isLoading && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#EF4444" />
            <Text style={styles.loadingText}><T>Loading order details...</T></Text>
          </View>
        )}

        <FormProvider {...methods}>
          {/* KOT Selection */}
          <View style={styles.modernCard}>
            <BTComboBox
              name="co_kot"
              label="Select KOT No"
              data={kots}
              placeholder={tp("Select KOT Number")}
              onValChange={(v: any) => setSelKot(v)}
            />
          </View>

          {/* Order Items List */}
          {fields.length > 0 && !isLoading && (
            <View style={styles.modernCard}>
              {/* Header */}
              <View style={styles.cancelOrderHeader}>
                <Text style={styles.cancelOrderHeaderTextLeft}>
                  <T>Description</T>
                </Text>
                <Text style={styles.cancelOrderHeaderTextCenter}>
                  <T>Order Qty</T>
                </Text>
                <Text style={styles.cancelOrderHeaderTextCenter}>
                  <T>Cancel Qty</T>
                </Text>
              </View>

              {/* Items */}
              {fields.map((v: any, i: number) => {
                const currentQty = Number(
                  methods.getValues(`co_details.${i}.cd_qty`),
                );
                const originalQty = Number(
                  methods.getValues(`co_details.${i}.cd_oqty`),
                );
                const canDecrease = currentQty > 0;
                const canIncrease = currentQty < originalQty;

                return (
                  <View key={i} style={styles.cancelOrderItem}>
                    <View style={styles.cancelOrderItemDescription}>
                      <Text style={styles.cancelOrderItemDescriptionText}>
                        {v.cd_descr}
                      </Text>
                    </View>
                    <View style={styles.cancelOrderItemQty}>
                      <Text style={styles.cancelOrderItemText}>
                        {v.cd_oqty}
                      </Text>
                    </View>
                    <View style={styles.cancelOrderItemControls}>
                      <TouchableOpacity
                        onPress={() => handleDecreaseQty(i)}
                        disabled={!canDecrease}
                        activeOpacity={0.8}
                        style={[
                          styles.qtyButton,
                          !canDecrease && styles.qtyButtonDisabled,
                        ]}>
                        <Icon name="minus" size={16} color="#FFFFFF" />
                      </TouchableOpacity>
                      <View style={styles.qtyInput}>
                        <TextInput
                          name={`co_details.${i}.cd_qty`}
                          label=""
                          placeholder="0"
                          keyboardType="numeric"
                          style={{
                            fontSize: 15,
                            fontWeight: '700',
                            color: '#111827',
                            textAlign: 'center',
                            width: '100%',
                            height: '100%',
                          }}
                          onChangeText={txt => {
                            if (Number(txt) > Number(v.cd_oqty)) {
                              methods.setValue(`co_details.${i}.cd_qty`, '');
                            }
                          }}
                        />
                      </View>
                      <TouchableOpacity
                        onPress={() => handleIncreaseQty(i)}
                        disabled={!canIncrease}
                        activeOpacity={0.8}
                        style={[
                          styles.qtyButton,
                          !canIncrease && styles.qtyButtonDisabled,
                        ]}>
                        <Icon name="plus" size={16} color="#FFFFFF" />
                      </TouchableOpacity>
                    </View>
                  </View>
                );
              })}
            </View>
          )}

          {/* Remarks */}
          {fields.length > 0 && (
            <View style={styles.modernCard}>
              <TextInput
                name="co_remarks"
                label="Remarks (Required)"
                placeholder={tp("Enter cancellation remarks")}
                multiline
                numberOfLines={3}
              />
            </View>
          )}

          {/* Action Buttons */}
          {fields.length > 0 && (
            <View style={styles.actionButtonsWrapper}>
              <View style={styles.actionButtonsContainer}>
                <View style={styles.actionButtonWrapper}>
                  <BTButton
                    title="Cancel Order"
                    color="#EF4444"
                    onPress={methods.handleSubmit(onSubmit, onError)}
                    onLongPress={() => {}}
                    disabled={isSaving}
                    style={styles.actionButtonStyle}
                  />
                </View>
                <View style={styles.actionButtonWrapper}>
                  <BTButton
                    title="Reset"
                    color="#64748B"
                    onPress={() => methods.reset()}
                    onLongPress={() => {}}
                    disabled={isSaving}
                    style={styles.actionButtonStyle}
                  />
                </View>
              </View>
            </View>
          )}
        </FormProvider>
    </ScrollView>
  );
};

export default POSCancelKOT;

